import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientHomePlanHydrated, ClientHomePlanDiscount } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan, ClientResolvedBrochureSource } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationHydrated } from "api-types/locations/locationsResponses";
import type { ClientLotHydrated, ClientLotDiscount } from "api-types/lots/lotsResponses";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { BuilderDocumentType } from "api-types/modelTypes";
import type { PlanSetTemplateIdentifier } from "building-types/views/planSets";
import { ensureDefined } from "common/utils/assertOfType";
import builderModalStyles from "lib/components/builders/communities/plans/modals/BuilderModal.module.scss";
import { locationAncestorsForLocationId } from "lib/components/builders/communities/utils/locationUtils";
import { useExportSalesBrochureEvents } from "lib/components/builders/communities/utils/useExportPlanEvents";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import computeDefaultPlanSetExportFileName from "lib/components/design/defaultPlanSetExportFileName";
import { PlanSetComputeService } from "lib/components/studio/editorViewer/planSetComputeService";
import type { DownloadInitializer } from "lib/hooks/useDownloadState";
import { augmentBrochureSettings } from "lib/state/brochureSettings";
import { useUserContext } from "lib/state/UserContextProvider";
import type {
  BasePlanSetDownloadParams,
  DownloadPlanSetTemplateParams,
  SalesBrochureDownloadParams,
} from "lib/workers/pdfDownloads";
import type { ModalProps } from "ui/components/Modal";
import DownloadProgressPlanModal from "./modal/DownloadProgressPlanModal";

const downloadSalesBrochure: DownloadInitializer<SalesBrochureDownloadParams> = async (params) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadSalesBrochure(params);
};

const downloadPlanSetTemplate: DownloadInitializer<BasePlanSetDownloadParams> = async (params) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadPlanSetTemplate(params);
};

interface DownloadBrochureModalProps extends ModalProps {
  homePlan: ClientHomePlanHydrated;
  builder?: ClientBuilder;
  locationPlan?: ClientLocationPlan;
  /** Absent keeps the built-in path. */
  brochureSource?: ClientResolvedBrochureSource;
  masterPlan?: ClientMasterPlan;
  location?: ClientLocationHydrated;
  lot?: ClientLotHydrated;
  displayRole: string;
  lotDiscounts: ClientLotDiscount[];
  homePlanDiscounts: ClientHomePlanDiscount[];
  isPublic: boolean;
}

export default function DownloadBrochureModal({
  builder,
  homePlan,
  locationPlan,
  brochureSource,
  masterPlan,
  location,
  lot,
  open,
  onClose,
  displayRole,
  lotDiscounts,
  homePlanDiscounts,
  isPublic,
}: DownloadBrochureModalProps) {
  const { optionChoices, planSetId, doc } = homePlan;
  const { user, enabledFeatures } = useUserContext();
  const { data: locationHierarchy, isLoading: hierarchyLoading } = useLocationHierarchy(
    isPublic ? "" : builder?.id ?? "",
    isPublic ? "" : location?.id ?? "",
  );
  const brochureSettings = augmentBrochureSettings({
    builder,
    location,
    locationHierarchy,
    hierarchyLoading,
    isPublic,
  });

  const sendSalesBrochureAnalytics = useExportSalesBrochureEvents(homePlan, locationHierarchy);
  const salesStaff = homePlan?.salesStaff ?? location?.salesStaff ?? user ?? null;
  const locationAncestors = locationAncestorsForLocationId(locationHierarchy, location?.id);
  const waitingForLocationHierarchy = !isPublic && Boolean(location?.id) && hierarchyLoading;

  if (waitingForLocationHierarchy) {
    return null;
  }

  const resolvedSource = brochureSource?.source;

  if (resolvedSource?.sourceType === "none") {
    return null;
  }

  if (resolvedSource?.sourceType === "plan_set_template" && resolvedSource.planSetTemplateId) {
    const planSetTemplateIdentifier: PlanSetTemplateIdentifier = {
      type: "CUSTOM",
      templateId: resolvedSource.planSetTemplateId,
    };
    const downloadParams: DownloadPlanSetTemplateParams = {
      planType: "home_plan",
      builder: ensureDefined(builder),
      titleSheetInfo: {
        builder,
        homePlan,
        masterPlan,
        communityPlan: homePlan.detachedAt ? undefined : locationPlan,
        community: location,
        locationAncestors,
        lot,
      },
      plan: homePlan,
      planName: planSetId,
      planSetTemplateIdentifier,
      parameterValueOverrides: {},
      filterDisabledOptionSetOverrides: false,
      exportFileName: computeDefaultPlanSetExportFileName({
        doc,
        planSetTemplateIdentifier,
        exportContextName: planSetId,
      }),
      service: PlanSetComputeService.getInstance(),
      lotDiscounts,
      homePlanDiscounts,
      enabledFeatures,
      isPublic,
    };

    return (
      <DownloadProgressPlanModal<DownloadPlanSetTemplateParams>
        open={open}
        downloadParams={downloadParams}
        initDownload={downloadPlanSetTemplate}
        onClose={onClose}
        documentType={BuilderDocumentType.SALES_BROCHURE}
        sendAnalytics={sendSalesBrochureAnalytics}
        modalClassName={builderModalStyles.wrapper}
      />
    );
  }

  return (
    <DownloadProgressPlanModal<SalesBrochureDownloadParams>
      open={open}
      downloadParams={{
        homePlan,
        builder,
        location,
        locationAncestors,
        optionChoices: optionChoices ?? undefined,
        doc,
        planSetId,
        salesStaff,
        brochureSettings,
        displayRole,
        lot,
        locationPlan,
        masterPlan,
        lotDiscounts,
        homePlanDiscounts,
        isPublic,
        selectedReportingUnitId: lot?.reportingUnitId ?? homePlan.lot?.reportingUnitId ?? undefined,
        enabledFeatures,
      }}
      initDownload={downloadSalesBrochure}
      onClose={onClose}
      documentType={BuilderDocumentType.SALES_BROCHURE}
      sendAnalytics={sendSalesBrochureAnalytics}
      modalClassName={builderModalStyles.wrapper}
    />
  );
}
